import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import moment from 'moment';
import { css } from '@emotion/react';

import { ADMIN_ACCESS_TOKEN_KEY, PRODUCT_TYPE, getFetcher } from '../../../App';
import { IdValidationStyle } from '@pages/Login/style';
import { Input, PrimaryButton } from '@components/atoms';
import { Sub1Button } from '@components/atoms';
import { UserPermission } from '@components/organisms';
import { dialogContentsState, Popup } from '@components/templates';
import { REGEX_EMAIL_TEXT2 } from '@components/templates/Fields/MyAccount';
import { CloseButtonStyle } from '@components/organisms/PageHeader';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { NAME_REGEX, PWD_REGEX, REGEX_KOREAN, REGEX_WRONG_SPECIAL } from '@variables/utils';
import { InformationType } from '@models/license/licence';
import { submitDebounce } from '../../../utils/lib';
import {
  CommonValidationStyle,
  CreateAccountWrapper,
  CreateButtonStyle,
  EmailValidationStyle,
  NameValidationStyle,
  PwValidationStyle,
  UserInfoSection,
} from './styles';

export const checkName = (name: string) => {
  if (!NAME_REGEX.test(name)) {
    return 'invalid';
  }

  return '';
};
export const checkEmail = (email: string, isCloud = false) => {
  if (!email) {
    return 'invalid';
  } else if (!REGEX_EMAIL_TEXT2.test(email)) {
    return isCloud ? 'regex-invalid' : 'invalid';
  }

  return '';
};
export const checkId = (id: string) => {
  const ID_REGEX = /^([a-z|0-9]+)$/;

  if (!id) {
    return 'none';
  } else if (id.length < 3) {
    return 'short';
  } else if (id.length > 50) {
    return 'invalid';
  } else if (!ID_REGEX.test(id)) {
    return 'regex-invalid';
  }

  return '';
};
export const checkPassword = (password: string, userId: string, isCloud = false) => {
  if (!password) {
    return 'true';
  } else if (!isCloud && password === userId) {
    return 'same-with-id';
  } else if (isCloud && password === userId) {
    // 이때 userId 는 email 이다.
    return 'same-with-email';
  } else if (password.length < 10) {
    return 'regrex';
  } else if (REGEX_KOREAN.test(password) || !PWD_REGEX.test(password) || REGEX_WRONG_SPECIAL.test(password)) {
    return 'regrex';
  }
  return '';
};

const addYearOnCurrent = () => {
  return moment().add(1, 'year').format('YYYY-MM-DD');
};

const CreateAccount = () => {
  const random = React.useRef(Date.now());

  const [accessToken, setAccessToken] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [toDate, setToDate] = useState(addYearOnCurrent());
  const [userId, setUserId] = useState('');
  const [shouldFetch, setShouldFetch] = useState(true);
  const [infoData, setInfoData] = useState<InformationType | null>(null);
  const [permissionCombination, setPermissionCombination] = useState('READ_ALL_DELETE_ALL');
  const [createDisabled, setCreateDisabled] = useState(true);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const [validAll, setValidAll] = useState({
    id: false,
    email: false,
    pwd: false,
    fstName: false,
    lastName: false,
  });

  const [fetchingData, setFetchingData] = useState(false);
  const url = `api/license`;

  const { data, error } = useSWR(shouldFetch && !!accessToken.length ? [{ url, accessToken }, random] : null);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  useEffect(() => {
    if (data?.result && shouldFetch) {
      setShouldFetch(false);
      setInfoData(data.result as InformationType);
    }
  }, [data, shouldFetch]);

  const isCloud = PRODUCT_TYPE === 'cloud'; // cloud || onPremise

  const memoizedParams = useMemo(() => {
    return {
      email,
      firstName,
      lastName,
      password,
      toDate,
      userId,
    };
  }, [email, firstName, lastName, password, toDate, userId]);

  const checkSubmit = useCallback(() => {
    if (
      !checkName(firstName) &&
      !checkName(lastName) &&
      !checkEmail(email, isCloud) &&
      toDate &&
      !validAll.email &&
      !validAll.pwd &&
      !validAll.fstName &&
      !validAll.lastName
    ) {
      // check Cloud
      if (isCloud && !checkPassword(password, email, isCloud)) {
        setCreateDisabled(false);
      } else if (!isCloud && !checkId(userId) && !checkPassword(password, userId) && !validAll.id) {
        setCreateDisabled(false);
      } else {
        setCreateDisabled(true);
      }
    } else {
      setCreateDisabled(true);
    }
  }, [userId, firstName, lastName, password, email, toDate, validAll, isCloud]);

  const createAccount = async () => {
    if (!fetchingData) {
      setFetchingData(true);
      if (isCloud) {
        memoizedParams.userId = memoizedParams.email;
      }

      const response = await getFetcher({
        method: 'POST',
        accessToken,
        body: {
          ...memoizedParams,
          permissionCombination,
        },
      })(`api/user`);

      if (response.code === '200') {
        setDialogContents({
          type: 'success',
          body: 'Account has been created successfully.',
          footer: <PrimaryButton onClick={() => window.close()}>Confirm</PrimaryButton>,
        });
        setFetchingData(false);
      } else {
        let header = '';
        let body = '';
        if (ErrorCode[response.code]) {
          header = `${ErrorCode[response.code]}`;
          body = `${ErrorMessage[ErrorCode[response.code]]}`;
        } else {
          header = response.code;
        }
        setFetchingData(false);
        setDialogContents({
          type: 'error',
          header: `${header}`,
          body: <>{body}</>,
          footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
        });
      }
    }
  };

  /**
   * blur시 submit 변경 처리
   */
  useEffect(() => {
    checkSubmit();
  }, [validAll]);

  useEffect(() => {
    const targetBody = document.querySelector('body');
    if (targetBody) targetBody?.classList.add('createPop');
  }, []);

  return (
    <Popup
      header={{
        title: 'Create account',
      }}
      body={
        <CreateAccountWrapper>
          <UserInfoSection className={`user-info-section`}>
            {isCloud ? (
              <Input
                type={'text'}
                placeholder={'ID(Email address)'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                styles={css`
                  ${CommonValidationStyle};
                  ${EmailValidationStyle};
                  grid-area: id;
                `}
                onBlur={(e) => {
                  let clone = JSON.parse(JSON.stringify(validAll));
                  const result = checkEmail(e.target.value, isCloud);
                  e.target.dataset['invalid'] = result;

                  if (result) {
                    clone.email = true;
                    setValidAll(clone);
                  } else {
                    clone.email = false;
                    setValidAll(clone);
                  }
                }}
                required
              />
            ) : (
              <Input
                type="text"
                placeholder={'ID'}
                value={userId}
                onChange={(e) => {
                  e.target.dataset['invalid'] = '';
                  let value = e.target.value.toLowerCase();

                  if (value.length > 50) {
                    setUserId(value.slice(0, 50));
                    return;
                  }

                  setUserId(value);
                }}
                styles={css`
                  ${IdValidationStyle};
                  grid-area: id;
                `}
                onBlur={(e) => {
                  let clone = JSON.parse(JSON.stringify(validAll));
                  const result = checkId(e.target.value);
                  e.target.dataset['invalid'] = result;
                  if (result) {
                    clone.id = true;
                    setValidAll(clone);
                  } else {
                    clone.id = false;
                    setValidAll(clone);
                  }
                }}
                required
              />
            )}

            <Input
              type="text"
              placeholder={'First name'}
              value={firstName}
              onChange={(e) => {
                e.target.dataset['invalid'] = '';
                setFirstName(e.target.value);
              }}
              styles={css`
                ${CommonValidationStyle};
                ${NameValidationStyle};
                grid-area: fname;
              `}
              onBlur={(e) => {
                let clone = JSON.parse(JSON.stringify(validAll));
                const result = checkName(e.target.value);
                e.target.dataset['invalid'] = result;
                if (result) {
                  clone.fstName = true;
                  setValidAll(clone);
                } else {
                  clone.fstName = false;
                  setValidAll(clone);
                }
              }}
              required
            />
            <Input
              type="text"
              placeholder={'Last name'}
              value={lastName}
              onChange={(e) => {
                e.target.dataset['invalid'] = '';
                setLastName(e.target.value);
              }}
              styles={css`
                ${CommonValidationStyle};
                ${NameValidationStyle};
                grid-area: lname;
              `}
              onBlur={(e) => {
                let clone = JSON.parse(JSON.stringify(validAll));
                const result = checkName(e.target.value);
                e.target.dataset['invalid'] = result;
                if (result) {
                  clone.lastName = true;
                  setValidAll(clone);
                } else {
                  clone.lastName = false;
                  setValidAll(clone);
                }
              }}
              required
            />
            <Input
              className={'pwd'}
              type="password"
              placeholder={'Password'}
              value={password}
              onChange={(e) => {
                if (e.target.value.length > 50) return;
                setPassword(e.target.value);
              }}
              styles={css`
                ${CommonValidationStyle};
                ${PwValidationStyle};
                grid-area: pwd;
              `}
              onBlur={(e) => {
                let clone = JSON.parse(JSON.stringify(validAll));
                const username = isCloud ? email : userId;
                const result = checkPassword(e.target.value, username, isCloud);
                e.target.dataset['invalid'] = result;
                if (result) {
                  clone.pwd = true;
                  setValidAll(clone);
                } else {
                  clone.pwd = false;
                  setValidAll(clone);
                }
              }}
              required
            />
          </UserInfoSection>
          <label className={'tit-user'}>Permissions to User</label>
          <UserPermission targetPermission={permissionCombination} setTargetPermission={setPermissionCombination} />

          {isCloud ? null : (
            <Input
              className={isCloud ? 'cloud' : 'on-premise'}
              type={'text'}
              placeholder={'Email address'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              styles={css`
                ${CommonValidationStyle};
                ${EmailValidationStyle};
                margin: 15px 0;
              `}
              onBlur={(e) => {
                let clone = JSON.parse(JSON.stringify(validAll));
                const result = checkEmail(e.target.value, isCloud);
                e.target.dataset['invalid'] = result;
                if (result) {
                  clone.email = true;
                  setValidAll(clone);
                } else {
                  clone.email = false;
                  setValidAll(clone);
                }
              }}
              required
            />
          )}

          <Input
            type={'date'}
            placeholder={'~ Account valid duration(End date)'}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            styles={css`
              margin: 15px 0;
            `}
            props={{
              min: new Date().toISOString().split('T')[0],
            }}
          />
        </CreateAccountWrapper>
      }
      footer={
        <>
          <Sub1Button onClick={() => window.close()} styles={CloseButtonStyle}></Sub1Button>
          <PrimaryButton disabled={createDisabled} onClick={submitDebounce(createAccount)} styles={CreateButtonStyle}>
            Create
          </PrimaryButton>
        </>
      }
    />
  );
};

export default CreateAccount;
